import { Button, Card, CardContent, CardHeader, CardTitle, ScrollArea, Search, Spinner, TreeList } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { ArrowRightIcon, FileTextIcon, FolderIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBehaviorFactorsTree } from '@/entities/knowledge';
import { useBreakpoints, useDebouncedValue } from '@/shared/hooks';

type BehaviorFactorTreeProps = {
  selectedId?: number;
  onSelectItem: (id: number) => void;
  canCreate: boolean;
  onCreateFactor: () => void;
};

export const BehaviorFactorTree: FC<BehaviorFactorTreeProps> = ({
  selectedId,
  onSelectItem,
  canCreate,
  onCreateFactor,
}) => {
  const { t } = useTranslation('behaviorFactors');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const bp = useBreakpoints();

  const { data, isLoading } = useBehaviorFactorsTree({
    search: debouncedSearch || undefined,
    isDeleted: false,
  });

  const items = data?.items ?? [];

  const filteredItems = useMemo(() => {
    if (!debouncedSearch.trim()) return items;

    const term = debouncedSearch.toLowerCase();
    return items.filter((factor) => {
      const factorMatches = factor.name.toLowerCase().includes(term);
      const childMatches = factor.children?.some((child) => child.name.toLowerCase().includes(term));
      return factorMatches || childMatches;
    });
  }, [items, debouncedSearch]);

  const activeNodeId = selectedId !== undefined ? String(selectedId) : undefined;

  /* На десктопе (>= lg) клик по заголовку группы открывает карточку.
     На мобильном клик только раскрывает аккордеон — навигация через rightSlot-кнопку. */
  const handleGroupClick = (id: number) => {
    if (bp.lg) {
      onSelectItem(id);
    }
  };

  return (
    <Card className="flex h-full w-full flex-col gap-0 overflow-hidden pb-0">
      {/* Заголовок */}
      <CardHeader className="flex h-auto flex-col gap-4 border-b border-border bg-card p-4">
        <div className="flex w-full items-center gap-4">
          <CardTitle className="text-base font-bold">{t('title')}</CardTitle>
          {canCreate && (
            <Button variant="default" size="sm" className="ml-auto" onClick={onCreateFactor}>
              {t('addFactor')}
            </Button>
          )}
        </div>

        <Search
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </CardHeader>

      {/* Дерево */}
      <CardContent className="h-full overflow-hidden p-0 px-2">
        <ScrollArea className="h-full">
          <div className="py-2">
            {isLoading && (
              <div className="flex items-center justify-center p-8">
                <Spinner className="size-6" />
              </div>
            )}

            {!isLoading && filteredItems.length === 0 && (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {searchTerm ? t('noSearchResults') : t('noFactors')}
              </div>
            )}

            {!isLoading && filteredItems.length > 0 && (
              <TreeList.Root value={expandedIds} onValueChange={setExpandedIds} activeNodeId={activeNodeId}>
                {filteredItems.map((factor) =>
                  factor.children && factor.children.length > 0 ? (
                    /* Фактор с субфакторами — раскрываемая группа */
                    <TreeList.Group
                      key={factor.id}
                      value={String(factor.id)}
                      label={
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <FolderIcon className="size-4 shrink-0" />
                          <span>{factor.name}</span>
                        </div>
                      }
                      onClick={() => handleGroupClick(factor.id)}
                      rightSlot={
                        /* Кнопка перехода к карточке — только на мобильном */
                        <IconButton
                          variant="ghost"
                          size="icon"
                          className="size-6 lg:hidden"
                          label={t('openCard')}
                          icon={<ArrowRightIcon className="size-4" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectItem(factor.id);
                          }}
                        />
                      }
                    >
                      {factor.children.map((subfactor) => (
                        <TreeList.Item
                          key={subfactor.id}
                          value={String(subfactor.id)}
                          onClick={() => onSelectItem(subfactor.id)}
                        >
                          <div className="flex items-center gap-2">
                            <FileTextIcon className="size-4 shrink-0" />
                            <span>{subfactor.name}</span>
                          </div>
                        </TreeList.Item>
                      ))}
                    </TreeList.Group>
                  ) : (
                    /* Фактор без субфакторов — лист */
                    <TreeList.Item key={factor.id} value={String(factor.id)} onClick={() => onSelectItem(factor.id)}>
                      <div className="flex items-center gap-2">
                        <FolderIcon className="size-4 shrink-0" />
                        <span className="text-sm font-medium">{factor.name}</span>
                      </div>
                    </TreeList.Item>
                  ),
                )}
              </TreeList.Root>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
